import { Assets, Container, Graphics, Text } from "pixi.js";

import { Scene } from "./Scene";

const TITLE = "IRON SAILS";
const TITLE_SIZE = 72;

const BAR_WIDTH = 320;
const BAR_HEIGHT = 6;
/** Gap between the bottom of the title and the bar. */
const BAR_OFFSET_Y = 40;
/** How fast the drawn bar chases the real load progress, as a fraction closed per second. */
const BAR_EASING = 7;

const TRACK_COLOR = 0x123c4a;
const FILL_COLOR = 0xf2e2c4;

/**
 * First screen. Loads the boot bundle (the title font) so it has something to show, then
 * loads the game bundle behind a progress bar.
 */
export class Preloader extends Scene {
    private readonly _group = new Container();
    private _bar: Graphics | null = null;
    private _progress = 0;
    private _targetProgress = 0;

    public async enter(width: number, height: number): Promise<void> {
        this._container.addChild(this._group);

        // `src/assets/` is Vite's publicDir, so its contents are served at the site root —
        // there is no "assets/" segment in the URL. See vite.config.ts.
        await Assets.init({ basePath: "", manifest: "Manifest.json" });
        await Assets.loadBundle("boot");

        this._buildUI();
        this.resize(width, height);

        await Assets.loadBundle("game", (progress) => {
            this._targetProgress = progress;
        });
    }

    public override update(deltaSeconds: number): void {
        if (!this._bar) return;

        // Clamped so a long frame cannot overshoot the target and swing the bar backwards.
        const step = Math.min(BAR_EASING * deltaSeconds, 1);
        this._progress += (this._targetProgress - this._progress) * step;
        this._drawBar();
    }

    public override resize(width: number, height: number): void {
        this._group.position.set(width / 2, height / 2);
    }

    private _buildUI(): void {
        const title = new Text({
            text: TITLE,
            anchor: 0.5,
            style: {
                fontFamily: "Grobold",
                fontSize: TITLE_SIZE,
                fill: FILL_COLOR,
                letterSpacing: 4
            }
        });
        this._group.addChild(title);

        const track = new Graphics().roundRect(-BAR_WIDTH / 2, 0, BAR_WIDTH, BAR_HEIGHT, BAR_HEIGHT / 2).fill(TRACK_COLOR);
        track.y = title.height / 2 + BAR_OFFSET_Y;
        this._group.addChild(track);

        this._bar = new Graphics();
        this._bar.position.set(-BAR_WIDTH / 2, track.y);
        this._group.addChild(this._bar);

        this._drawBar();
    }

    private _drawBar(): void {
        if (!this._bar) return;

        this._bar.clear();

        // roundRect with a width below its own corner radius renders nothing.
        const filled = this._progress * BAR_WIDTH;
        if (filled < BAR_HEIGHT) return;

        this._bar.roundRect(0, 0, filled, BAR_HEIGHT, BAR_HEIGHT / 2).fill(FILL_COLOR);
    }
}
